import {
  DriverDetail,
  Leaderboard,
  RaceRules,
  RacePlan,
  StrategyActuals,
  StrategyPlan,
  StrategySummary,
} from '../types';

export const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ?? 'http://localhost:3001';

const TOKEN_STORAGE_KEY = 'karting-timing-token';

export type UserRole = 'admin' | 'user';

export interface AuthUser {
  id: string;
  username: string;
  displayName: string;
  role: UserRole;
  /** The one account named in the backend's AUTH_SUPER_ADMIN; sees every strategy. */
  isSuperAdmin: boolean;
}

/** The fuller shape the admin screen works with. */
export interface ManagedUser extends AuthUser {
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface CreateUserInput {
  username: string;
  password: string;
  displayName?: string;
  role?: UserRole;
}

export interface UpdateUserInput {
  displayName?: string;
  password?: string;
  role?: UserRole;
  isActive?: boolean;
}

/**
 * The token is mirrored here as well as in React state so that `authFetch` can be
 * called from anywhere without threading the auth context through every component.
 */
let currentToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function readStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function storeToken(token: string | null): void {
  currentToken = token;
  try {
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  } catch {
    // Private-mode browsers: the in-memory copy still works for this tab.
  }
}

export function getToken(): string | null {
  return currentToken;
}

/** Invoked when the backend rejects our token, so the app can drop to the login screen. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json();
    const message = body?.message;
    if (Array.isArray(message) && message.length > 0) return String(message[0]);
    if (typeof message === 'string' && message) return message;
  } catch {
    // Non-JSON error body — fall through.
  }
  return fallback;
}

/**
 * `fetch` against the backend with the bearer token attached. A 401 clears the
 * session and rejects, so callers only ever see a signed-in response or an error.
 */
export async function authFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (currentToken) headers.set('Authorization', `Bearer ${currentToken}`);

  const res = await fetch(`${BACKEND_URL}${path}`, { ...init, headers });

  if (res.status === 401) {
    unauthorizedHandler?.();
    throw new Error(await errorMessage(res, 'Your session has expired'));
  }
  if (!res.ok) {
    throw new Error(await errorMessage(res, `Request failed (${res.status})`));
  }
  return res;
}

/**
 * `authFetch` for callers that treat a status as data rather than a failure.
 *
 * The pit board's optimistic-concurrency path needs to see a 409 and adopt the
 * board that came back with it; `authFetch` throws on any non-OK response, so
 * that branch could never run. 401 still throws — an expired session is not
 * something a caller can act on.
 */
export async function authFetchStatus(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (currentToken) headers.set('Authorization', `Bearer ${currentToken}`);

  const res = await fetch(`${BACKEND_URL}${path}`, { ...init, headers });

  if (res.status === 401) {
    unauthorizedHandler?.();
    throw new Error(await errorMessage(res, 'Your session has expired'));
  }
  return res;
}

export async function login(
  username: string,
  password: string,
): Promise<{ token: string; user: AuthUser }> {
  const res = await fetch(`${BACKEND_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Could not sign in'));
  }
  return res.json();
}

/** Validates a stored token on boot and returns who it belongs to. */
export async function fetchCurrentUser(): Promise<AuthUser> {
  const res = await authFetch('/auth/me');
  return res.json();
}

/**
 * Mints a short-lived token for the raw-log download, which happens via a plain
 * browser navigation and so cannot carry an Authorization header.
 */
export async function fetchDownloadToken(): Promise<string> {
  const res = await authFetch('/auth/download-token');
  const body = await res.json();
  return body.token as string;
}

export type BulkEventAction = 'delete' | 'compress' | 'decompress';

export interface BulkEventResult {
  id: string;
  ok: boolean;
  error?: string;
}

export interface UpdateEventInput {
  title?: string;
  trackName?: string;
  host?: string;
  excludeFromRankings?: boolean;
}

export async function updateEvent<T>(
  id: string,
  input: UpdateEventInput,
): Promise<T> {
  const res = await authFetch(`/events/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json();
}

export async function fetchRacePlan(eventId: string): Promise<RacePlan> {
  const res = await authFetch(`/events/${eventId}/plan`);
  if (!res.ok) throw new Error(`Could not load the plan (HTTP ${res.status})`);
  return res.json();
}

/** Admin-only; the server rejects it otherwise. */
export async function saveRacePlan(
  eventId: string,
  plan: RacePlan,
): Promise<RacePlan> {
  const res = await authFetch(`/events/${eventId}/plan`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(plan),
  });
  if (!res.ok) {
    // The API explains what is wrong with the plan; surface that, not a code.
    const detail = await res.json().catch(() => null);
    throw new Error(detail?.message ?? `Could not save the plan (HTTP ${res.status})`);
  }
  return res.json();
}

export async function deleteEvent(id: string): Promise<void> {
  await authFetch(`/events/${id}`, { method: 'DELETE' });
}

/** Gzips or gunzips the event's raw log in place, returning the refreshed event. */
export async function setEventCompressed<T>(
  id: string,
  compressed: boolean,
): Promise<T> {
  const res = await authFetch(
    `/events/${id}/${compressed ? 'compress' : 'decompress'}`,
    { method: 'POST' },
  );
  return res.json();
}

/** One action over many events; each id succeeds or fails on its own. */
export async function bulkEventAction(
  ids: string[],
  action: BulkEventAction,
): Promise<BulkEventResult[]> {
  const res = await authFetch('/events/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, action }),
  });
  return res.json();
}

export async function listUsers(): Promise<ManagedUser[]> {
  const res = await authFetch('/admin/users');
  return res.json();
}

export async function createUser(input: CreateUserInput): Promise<ManagedUser> {
  const res = await authFetch('/admin/users', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json();
}

export async function updateUser(
  id: string,
  input: UpdateUserInput,
): Promise<ManagedUser> {
  const res = await authFetch(`/admin/users/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json();
}

/** The strategy planner's payload — everything but the server-owned fields. */
export interface StrategyInput {
  title: string;
  config: StrategyPlan['config'];
  roster: StrategyPlan['roster'];
  stints: StrategyPlan['stints'];
  /** The recorded race this plan is for, or null while it is only a plan. */
  eventId: string | null;
  /** The grid row and kart it runs for; both null while unassigned. */
  teamId: string | null;
  kartNumber: string | null;
  /**
   * Account ids to share with. Leave it out to keep the current list: only the
   * owner can change it, and everyone else's value is ignored.
   */
  sharedWith?: string[];
  /** `updatedAt` as last seen; a mismatch means somebody else saved first. */
  expectedUpdatedAt?: string | null;
}

/** Enough of an event to pick one; the full list endpoint returns more. */
export interface EventChoice {
  id: string;
  title: string;
  trackName: string;
  startedAt: string;
  endedAt: string | null;
}

/** Races on record, newest first — what a strategy can be attached to. */
export async function listEventChoices(): Promise<EventChoice[]> {
  const res = await authFetch('/events');
  return res.json();
}

/**
 * Strategies are visible to their owner, to the accounts they are shared with and
 * to the super admin; the server filters, so this is simply "the ones I can see".
 */
export async function listStrategies(eventId?: string): Promise<StrategySummary[]> {
  const query = eventId ? `?eventId=${encodeURIComponent(eventId)}` : '';
  const res = await authFetch(`/strategies${query}`);
  return res.json();
}

/** Every plan I can see for one race, whole, in a single request. */
export async function listStrategyPlans(eventId: string): Promise<StrategyPlan[]> {
  const res = await authFetch(`/strategies?eventId=${encodeURIComponent(eventId)}&full=true`);
  return res.json();
}

export async function fetchStrategy(id: string): Promise<StrategyPlan> {
  const res = await authFetch(`/strategies/${id}`);
  return res.json();
}

export async function createStrategy(input: StrategyInput): Promise<StrategyPlan> {
  const res = await authFetch('/strategies', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return res.json();
}

/**
 * Thrown when somebody else saved the strategy after you opened it. Carries the
 * copy that is stored now, so the caller can show what changed rather than just
 * saying no.
 */
export class StrategyConflictError extends Error {
  constructor(
    message: string,
    readonly current: StrategyPlan | null,
  ) {
    super(message);
    this.name = 'StrategyConflictError';
  }
}

export async function saveStrategy(
  id: string,
  input: StrategyInput,
): Promise<StrategyPlan> {
  // A status is data here, not a failure: 409 has the current copy in its body.
  const res = await authFetchStatus(`/strategies/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => null);
    throw new StrategyConflictError(
      body?.message ?? 'This strategy was changed by someone else',
      body?.current ?? null,
    );
  }
  if (!res.ok) throw new Error(await errorMessage(res, `Request failed (${res.status})`));
  return res.json();
}

export async function deleteStrategy(id: string): Promise<void> {
  await authFetch(`/strategies/${id}`, { method: 'DELETE' });
}

/**
 * Record what the timing feed showed. The server merges rather than replaces, so
 * any number of browsers can send the same stop without harm.
 */
export async function patchStrategyActuals(
  id: string,
  patch: StrategyActuals,
): Promise<StrategyPlan> {
  const res = await authFetch(`/strategies/${id}/actuals`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return res.json();
}

/** Forget everything recorded, so a plan can be reused for another race. */
export async function resetStrategyActuals(id: string): Promise<StrategyPlan> {
  const res = await authFetch(`/strategies/${id}/actuals`, { method: 'DELETE' });
  return res.json();
}

export interface DirectoryEntry {
  id: string;
  username: string;
  displayName: string;
}

/** Everyone a strategy could be shared with. Names only. */
export async function listUserDirectory(): Promise<DirectoryEntry[]> {
  const res = await authFetch('/users/directory');
  return res.json();
}

/**
 * Push a rule set onto the event being recorded. Admin-only; the server rejects
 * it otherwise. This is the only writer of `/events/:id/rules` in the client —
 * before the planner existed, every live screen ran on DEFAULT_RACE_RULES.
 */
export async function saveRaceRules(
  eventId: string,
  rules: RaceRules,
): Promise<RaceRules> {
  const res = await authFetch(`/events/${eventId}/rules`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rules),
  });
  return res.json();
}

export interface LeaderboardQuery {
  track?: string | null;
  year?: number | null;
  minLaps?: number | null;
}

/** The all-time driver leaderboard. The server catches up on new events first. */
export async function fetchLeaderboard(query: LeaderboardQuery = {}): Promise<Leaderboard> {
  const params = new URLSearchParams();
  if (query.track) params.set('track', query.track);
  if (query.year) params.set('year', String(query.year));
  if (query.minLaps && query.minLaps > 1) params.set('minLaps', String(query.minLaps));
  const qs = params.toString();
  const res = await authFetch(`/rankings${qs ? `?${qs}` : ''}`);
  return res.json();
}

export async function fetchDriverDetail(id: string): Promise<DriverDetail> {
  const res = await authFetch(`/rankings/drivers/${encodeURIComponent(id)}`);
  return res.json();
}

export async function renameDriver(id: string, displayName: string): Promise<DriverDetail> {
  const res = await authFetch(`/rankings/drivers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ displayName }),
  });
  return res.json();
}

/** Fold `sourceId` into `targetId`. Admin-only; ratings are replayed server side. */
export async function mergeDrivers(sourceId: string, targetId: string): Promise<DriverDetail> {
  const res = await authFetch('/rankings/merge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sourceId, targetId }),
  });
  return res.json();
}

/** Split one spelling off into its own driver. Returns the new driver. */
export async function detachAlias(aliasId: string): Promise<DriverDetail> {
  const res = await authFetch(`/rankings/aliases/${encodeURIComponent(aliasId)}/detach`, {
    method: 'POST',
  });
  return res.json();
}

export async function rebuildRankings(): Promise<{ rankedEvents: number }> {
  const res = await authFetch('/rankings/rebuild', { method: 'POST' });
  return res.json();
}
